import { connect } from 'react-redux';
import { compose } from 'redux';
import StudentPage from './StudentPage';
import {
  actions as assignmentsActions,
  selectors as assignmentsSelectors,
} from '$data/assignments';
import {
  actions as assessmentItemsActions,
  selectors as assessmentItemsSelectors,
} from '$data/assessmentItems';
import {
  selectors as participantsSelectors,
  actions as participantsActions,
} from '$data/participants';
import {
  actions as providersActions,
  selectors as providersSelectors,
} from '$data/providers';
import { selectors as coreSelectors } from '$data/core';
import launchTurnitinAssignment from '$utils/launchTurnitinAssignment';
import launchTurnitinSubmission from '$utils/launchTurnitinSubmission';
import mergeAssignmentsWithGrades from './utils/mergeAssignmentsWithGrades';
import withAnalyticsHoc from '$utils/withAnalyticsHoc';
import { VIEW_STUDENT_PAGE } from '$variables/analyticsEvents';
import { STUDENT } from '$variables/constants';

const createLaunchTurnitinAssignmentFunction = (state) => (assignmentId) => {
  const spaceId = coreSelectors.getCurrentSpace(state).id;
  const user = coreSelectors.getUser(state);

  const assignment = assignmentId
    ? assignmentsSelectors.getAssignmentById(state, assignmentId)
    : null;

  launchTurnitinAssignment({
    user,
    spaceId,
    assignment,
  });
};

const createLaunchTurnitinSubmissionFunction = (state) => (submissionId) => {
  const spaceId = coreSelectors.getCurrentSpace(state).id;

  launchTurnitinSubmission({
    spaceId,
    submissionId,
  });
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { studentId: matchStudentId },
    },
  }
) => {
  const viewAs = coreSelectors.getViewAs(state);

  // -- data is different between educator and student

  const studentId =
    viewAs === STUDENT
      ? coreSelectors.getUserId(state) // student does not have :studentId in route
      : matchStudentId;

  const student =
    viewAs === STUDENT
      ? coreSelectors.getUser(state)
      : participantsSelectors.getStudentInfo(state, studentId);

  const isFetchingParticipants =
    viewAs === STUDENT
      ? false
      : participantsSelectors.getIsFetchingParticipants(state);

  return {
    studentId,
    viewAs,
    student,
    spaceId: coreSelectors.getCurrentSpace(state).id,
    isLoading:
      assessmentItemsSelectors.getAssessmentItemsIsLoading(state) ||
      assignmentsSelectors.getAssignmentsIsLoading(state) ||
      assignmentsSelectors.getStudentGradesLoading(state) ||
      assessmentItemsSelectors.getIsFetchingGrades(state) ||
      providersSelectors.getProvidersIsLoading(state) ||
      isFetchingParticipants,
    error:
      assignmentsSelectors.getAssignmentsError(state) ||
      assignmentsSelectors.getStudentGradesError(state) ||
      assessmentItemsSelectors.getFetchGradesError(state) ||
      providersSelectors.getProvidersError(state),
    assessmentItems: assessmentItemsSelectors.getAssessmentItems(state),
    assignments: assignmentsSelectors.getParentAssignments(state),
    studentGrades: assignmentsSelectors.getStudentGrades(state, studentId),
    hasStudentGrades: assignmentsSelectors.getHasStudentGrades(
      state,
      studentId
    ),
    launchTurnitinAssignment: createLaunchTurnitinAssignmentFunction(state),
    launchTurnitinSubmission: createLaunchTurnitinSubmissionFunction(state),
    hasFetchedStudents: participantsSelectors.hasStudents(state),
    ltiProviders: providersSelectors.getProvidersById(state),
  };
};

const mapDispatchToProps = {
  fetchAssessmentItems: assessmentItemsActions.fetchAssessmentItems,
  fetchAssignments: assignmentsActions.fetchAssignments,
  fetchGradesForStudent: assignmentsActions.fetchGradesForStudent,
  fetchAIGradesForStudent: assessmentItemsActions.fetchStudentGrades,
  fetchParticipantsInSpace: participantsActions.fetchParticipantsInSpace,
  fetchLtiProviders: providersActions.fetchLtiProviders,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  assignments: stateProps.hasStudentGrades
    ? mergeAssignmentsWithGrades({
        assignments: stateProps.assignments,
        studentGrades: stateProps.studentGrades,
      })
    : stateProps.assignments,
});

const withAnalytics = withAnalyticsHoc({
  eventName: VIEW_STUDENT_PAGE,
});

const wrap = compose(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  withAnalytics
);

export default wrap(StudentPage);
